import { create } from "zustand";
import type { GearId, WorkSlot } from "@shared/contracts";

/**
 * INTERFACE STATE ONLY. Nothing here came from the server and nothing here may.
 *
 * Everything in this store is destroyed by a refresh and should be: which card is showing is
 * a position in a deck that is regenerated from a seed, and which dose is selected is a
 * choice about the card in front of you, not a setting. `no-server-state-in-store` fails the
 * build if this file imports a service, a queries module or the API client, and the structure
 * test fails it if a field starts looking like a cache.
 */

/**
 * THE TWO DOSES THE DECK DRAWS, AND WHAT THEY ARE CALLED IN A GYM.
 *
 * The engine produces THREE gears and every suggestion carries all three. The deck shows two:
 * **RX is `push`** and **Scaled is `mid`**. `easy` is drawn, paid for and deliberately not
 * offered here — it exists for a recovery day, which is a different question from the one this
 * screen asks ("what am I training today?").
 *
 * ⚠️ THIS IS A SCREEN DECISION, WHICH IS WHY IT LIVES IN A STORE AND NOT IN THE CONTRACT.
 * `startSessionSchema` takes the whole `GearId` set on purpose — the contract carries what the
 * engine produced and the screen decides what a person sees. A future screen offering all
 * three needs no service change.
 */
export const DECK_GEARS = { rx: "push", scaled: "mid" } as const satisfies Record<string, GearId>;

/** Which half of the toggle is filled. The value is the gear that gets posted. */
export type DeckGear = (typeof DECK_GEARS)[keyof typeof DECK_GEARS];

interface SessionsUiState {
  /**
   * HOW MANY CARDS HAVE BEEN THROWN AWAY — an index into the loaded deck, and the ONLY thing
   * a skip does.
   *
   * A skip posts nothing. There is no skip endpoint, no skip table and no decay rule, by
   * decision: the deck is computed from a seed, so "not this, right now" needs no memory. If
   * this number ever needs to survive a relaunch it becomes a preference, not a server write.
   */
  passed: number;
  gear: DeckGear;
  /**
   * Whether to show the "session logged" row above the deck. Set by the screen that finishes a
   * session and cleared the moment the athlete engages with the deck again — a confirmation
   * that outlives the thing it confirms reads as the app being confused about what you did.
   */
  justLogged: boolean;
  /**
   * WHICH EMPTY SLOT HAS THE ADD-WORK SHEET OPEN ON IT — `strength`, `accessory`, or null for
   * none. Interface state and nothing else: what is open, which is exactly what a store holds.
   *
   * ═══ 🚨 WHY IT IS IN THE STORE RATHER THAN IN THE SCREEN'S OWN `useState` ═══
   *
   * The sheet's `Browse` pushes a whole screen (frame `5a`), and that screen's `Cancel` goes
   * back TO THE SHEET rather than to the session behind it — which is what the frame says and
   * what somebody who opened it from there expects. A `useState` inside the session screen
   * cannot survive that: the screen is unmounted while the browser is on top of it, so coming
   * back would find the sheet closed and the athlete two steps out from where they were.
   *
   * ⚠️ IT IS DELIBERATELY NOT A URL SEARCH PARAM, which would also survive. A sheet is not a
   * place: a link to a session with a sheet already open is not a thing anybody should be able
   * to send, and it would make an ephemeral state addressable for the life of the app.
   */
  workSlot: WorkSlot | null;

  /** Move to the next card. Skipping and accepting both do this; neither writes anything. */
  pass: () => void;
  chooseGear: (gear: DeckGear) => void;
  /** Called by the screen that saved a session (BUD-292), never by the deck itself. */
  markLogged: () => void;
  dismissLogged: () => void;
  /** Open the add-work sheet on a slot, or close it with null. */
  openWorkSlot: (slot: WorkSlot | null) => void;
}

export const useSessionsUiStore = create<SessionsUiState>((set) => ({
  passed: 0,
  gear: DECK_GEARS.rx,
  justLogged: false,
  workSlot: null,

  pass: () => set((state) => ({ passed: state.passed + 1, justLogged: false })),
  chooseGear: (gear) => set({ gear }),
  markLogged: () => set({ justLogged: true }),
  dismissLogged: () => set({ justLogged: false }),
  openWorkSlot: (workSlot) => set({ workSlot }),
}));
